import { jwtDecode } from 'jwt-decode';

import { getAccessToken } from '@shared/auth/tokens';

/**
 * Whitelist of JWT claims worth attaching to a `query_error` event, when a
 * token exists at all. Extend this array to capture another claim later --
 * no other code changes. Deliberately a whitelist, not a blocklist of what
 * to strip: a blocklist is only as good as your imagination about what a
 * token might someday carry (these tokens commonly carry email and other
 * identifying claims beyond `cf:entitlements`); a whitelist can only ever
 * leak what's explicitly named here. Verification of this claim happens
 * server-side (the GraphQL server checks it) -- capturing it here is purely
 * for local debugging of a failed request, not a client-side permission
 * decision.
 */
export const OBSERVABILITY_TOKEN_CLAIMS = [
	// Two spellings on purpose, not a typo: tokens issued by Canvasflow's own
	// IdP carry `cf:entitlements`, while tenants who bring their own IdP via
	// third-party SSO carry an unprefixed `entitlements`. Only the prefixed
	// one was listed originally, so every `query_error` from a third-party-SSO
	// tenant shipped with `tokenClaims: undefined` -- blind on exactly the
	// PWAs that are hardest to reach for debugging.
	'cf:entitlements',
	'entitlements',
] as const;

/**
 * Never attach the raw token itself -- it's a live bearer credential, and
 * anything captured here is a candidate for the topic-17 zip export and any
 * future remote reporter. Decode failures are swallowed: a malformed or
 * expired token must not take down error reporting.
 */
export function getObservabilityTokenClaims():
	Record<string, unknown> | undefined {
	const token = getAccessToken();
	if (!token) {
		return undefined;
	}

	try {
		const decoded = jwtDecode<Record<string, unknown>>(token);
		const claims: Record<string, unknown> = {};
		for (const claim of OBSERVABILITY_TOKEN_CLAIMS) {
			if (claim in decoded) {
				claims[claim] = decoded[claim];
			}
		}
		return Object.keys(claims).length > 0 ? claims : undefined;
	} catch {
		return undefined;
	}
}
